// Storage Service for localStorage persistence
import type { Project } from '../types';

const STORAGE_KEY = 'magicpage_projects';
const STORAGE_VERSION = '1';

interface StorageData {
    version: string;
    projects: Project[];
    lastProjectNumber: number;
}

// Deserialize project and restore Date objects
function deserializeProject(json: string): Project {
    const project = JSON.parse(json);
    // Restore Date objects
    project.createdAt = new Date(project.createdAt);
    project.updatedAt = new Date(project.updatedAt);
    if (project.publishedAt) {
        project.publishedAt = new Date(project.publishedAt);
    }
    // Restore dates in elements
    project.elements = project.elements.map((el: { createdAt: string | number | Date; updatedAt: string | number | Date; }) => ({
        ...el,
        createdAt: new Date(el.createdAt),
        updatedAt: new Date(el.updatedAt),
    }));
    return project;
}

export function getStoredProjects(): Project[] {
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        if (!data) return [];

        const parsed: StorageData = JSON.parse(data);
        if (parsed.version !== STORAGE_VERSION) {
            console.warn('Storage version mismatch, resetting...');
            return [];
        }

        return parsed.projects.map(p => deserializeProject(JSON.stringify(p)));
    } catch (error) {
        console.error('Error loading projects from storage:', error);
        return [];
    }
}

export function saveProjects(projects: Project[]): void {
    try {
        const data: StorageData = {
            version: STORAGE_VERSION,
            projects,
            lastProjectNumber: getLastProjectNumber(projects),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
        console.error('Error saving projects to storage:', error);
    }
}

export function saveProject(project: Project): void {
    try {
        const projects = getStoredProjects();
        const existingIndex = projects.findIndex(p => p.id === project.id);

        if (existingIndex >= 0) {
            projects[existingIndex] = project;
        } else {
            projects.push(project);
        }

        saveProjects(projects);
    } catch (error) {
        console.error('Error saving project:', error);
    }
}

export function deleteProject(projectId: string): void {
    try {
        const projects = getStoredProjects().filter(p => p.id !== projectId);
        saveProjects(projects);
    } catch (error) {
        console.error('Error deleting project:', error);
    }
}

export function getProjectById(projectId: string): Project | null {
    try {
        const projects = getStoredProjects();
        const project = projects.find(p => p.id === projectId);
        return project ? deserializeProject(JSON.stringify(project)) : null;
    } catch (error) {
        console.error('Error getting project:', error);
        return null;
    }
}

// Get the next project number for auto-naming
export function getNextProjectNumber(): number {
    const projects = getStoredProjects();
    return getLastProjectNumber(projects) + 1;
}

function getLastProjectNumber(projects: Project[]): number {
    let maxNumber = 0;

    for (const project of projects) {
        const match = project.name.match(/^Progetto (\d+)$/);
        if (match) {
            const num = parseInt(match[1], 10);
            if (num > maxNumber) {
                maxNumber = num;
            }
        }
    }

    return maxNumber;
}

// Generate default project name
export function generateDefaultProjectName(): string {
    const nextNumber = getNextProjectNumber();
    return `Progetto ${nextNumber}`;
}

// Export projects as JSON file
export function exportProjectsToFile(projects: Project[]): void {
    const dataStr = JSON.stringify(projects, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `magicpage-projects-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

// Import projects from JSON file
export async function importProjectsFromFile(file: File): Promise<Project[]> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const projects = JSON.parse(e.target?.result as string);
                resolve(projects.map((p: string) => deserializeProject(JSON.stringify(p))));
            } catch (error) {
                reject(new Error('Invalid file format'));
            }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file);
    });
}